/**
 * spaceEnemyConstants.ts
 * Shared tuning + barrel for World 2 space enemies.
 *
 * Per-kind constants live in sibling files (Phase 5 Tech Debt — file
 * length cap split, see ROADMAP.md):
 *
 *   - `FighterDrone`     → {@link ./fighterDroneConstants}
 *   - `BomberDrone`      → {@link ./bomberDroneConstants}
 *   - `ShieldSatellite`  → {@link ./shieldSatelliteConstants}
 *   - `StarEater` scout  → {@link ./starEaterConstants}
 *
 * Every per-kind symbol is re-exported below so existing consumers
 * (`import { FIGHTER_DRONE_WIDTH } from '.../spaceEnemyConstants'`) keep
 * working unchanged. New code may import either through this barrel or
 * directly from the per-kind file — both paths reach the same value.
 *
 * This file still owns:
 *  - The {@link SpaceEnemyKind} discriminator (consumed by every
 *    spawner / pool / renderer).
 *  - The generic spawner constants (`SPACE_ENEMY_*`).
 *  - The per-tick kind-selection weights.
 *  - The `HomingMine` constants (Level 2-3 hazard, not in the per-kind
 *    enemy split scope).
 *
 * Phase 4 — World 2: Starfield Drift.
 * See ROADMAP.md Phase 4 "Space Engine".
 *
 * @module spaceEnemyConstants
 */

// ── Per-kind constant re-exports ─────────────────────────────────────
export {
  FIGHTER_DRONE_WIDTH,
  FIGHTER_DRONE_HEIGHT,
  FIGHTER_DRONE_HP,
  FIGHTER_DRONE_DRIFT_SPEED,
  FIGHTER_DRONE_WAVE_AMPLITUDE_PX,
  FIGHTER_DRONE_WAVE_FREQUENCY_HZ,
  FIGHTER_DRONE_LASER_WIDTH,
  FIGHTER_DRONE_LASER_HEIGHT,
  FIGHTER_DRONE_LASER_SPEED,
  FIGHTER_DRONE_FIRE_INTERVAL_S,
  FIGHTER_DRONE_FIRE_INITIAL_DELAY_S,
  FIGHTER_DRONE_LASER_OFFSCREEN_MARGIN,
  FIGHTER_DRONE_LASER_POOL_CAP,
} from './fighterDroneConstants';

export {
  BOMBER_DRONE_WIDTH,
  BOMBER_DRONE_HEIGHT,
  BOMBER_DRONE_HP,
  BOMBER_DRONE_DRIFT_SPEED,
  BOMBER_DRONE_WAVE_AMPLITUDE_PX,
  BOMBER_DRONE_WAVE_FREQUENCY_HZ,
  BOMBER_DRONE_BOMB_WIDTH,
  BOMBER_DRONE_BOMB_HEIGHT,
  BOMBER_DRONE_BOMB_DROP_VY_PX_S,
  BOMBER_DRONE_BOMB_FUSE_S,
  BOMBER_DRONE_DROP_INTERVAL_S,
  BOMBER_DRONE_DROP_INITIAL_DELAY_S,
  BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX,
  BOMBER_DRONE_BOMB_POOL_CAP,
  BOMBER_DRONE_SHRAPNEL_WIDTH,
  BOMBER_DRONE_SHRAPNEL_HEIGHT,
  BOMBER_DRONE_SHRAPNEL_SPEED_PX_S,
  BOMBER_DRONE_SHRAPNEL_COUNT,
  BOMBER_DRONE_SHRAPNEL_TTL_S,
  BOMBER_DRONE_SHRAPNEL_POOL_CAP,
} from './bomberDroneConstants';

export {
  SHIELD_SATELLITE_WIDTH,
  SHIELD_SATELLITE_HEIGHT,
  SHIELD_SATELLITE_HP,
  SHIELD_SATELLITE_DRIFT_SPEED,
  SHIELD_SATELLITE_WOBBLE_AMPLITUDE,
  SHIELD_SATELLITE_WOBBLE_FREQUENCY_HZ,
} from './shieldSatelliteConstants';

export {
  STAR_EATER_WIDTH,
  STAR_EATER_HEIGHT,
  STAR_EATER_HP,
  STAR_EATER_DRIFT_SPEED,
  STAR_EATER_DRIFT_PRE_DURATION_S,
  STAR_EATER_TELEGRAPH_DURATION_S,
  STAR_EATER_SCAN_DURATION_S,
  STAR_EATER_DASH_DURATION_S,
  STAR_EATER_DASH_SPEED,
  STAR_EATER_DASH_VY_MAX,
} from './starEaterConstants';
export type { StarEaterAiPhase } from './starEaterConstants';

// ── Shared discriminator ─────────────────────────────────────────────

/** Discriminator for `SpaceEnemyBase` subclasses — drives renderer + pool lookups. */
export type SpaceEnemyKind = 'fighter' | 'bomber' | 'satellite' | 'starEater';

// ── Generic spawner / pool ───────────────────────────────────────────

/** AGENTS.md caps pools at 50. 12 covers 2-1's onboarding density without exhausting. */
export const SPACE_ENEMY_MAX_ACTIVE = 12;

/**
 * Mean seconds between space-enemy spawns. Poisson-like spawner: each tick
 * has probability `dt / SPACE_ENEMY_SPAWN_INTERVAL_S` of producing one
 * enemy of *any* kind. On a hit a second RNG call walks the cumulative
 * weight ladder (see {@link SPACE_ENEMY_FIGHTER_WEIGHT} +
 * {@link SPACE_ENEMY_SATELLITE_WEIGHT}) to pick fighter / satellite /
 * bomber.
 *
 * At 2.0 s mean, a 60-second level produces ~30 enemies on average. With
 * the current 0.7 / 0.1 / 0.2 split that's roughly ~21 fighters, ~3
 * satellites, and ~6 bombers per minute — dense enough to feel like a
 * wave, sparse enough not to overwhelm with the asteroid spawner running
 * in parallel.
 */
export const SPACE_ENEMY_SPAWN_INTERVAL_S = 2.0;

/**
 * Horizontal spawn offset past the camera's right edge in px. Matches the
 * asteroid spawner so enemies pop in fully off-screen and never appear
 * mid-air inside the viewport.
 */
export const SPACE_ENEMY_SPAWN_X_OFFSET = 16;

/** Minimum Y (top of viewport) to spawn any space enemy — avoids the HUD strip. */
export const SPACE_ENEMY_SPAWN_Y_MIN = 24;

/** Vertical inset from the bottom of the viewport when picking a spawn Y. */
export const SPACE_ENEMY_SPAWN_Y_MAX_OFFSET = 24;

/**
 * Despawn margin past the left edge of the camera (px). Mirrors
 * `ASTEROID_OFFSCREEN_MARGIN` so enemies and asteroids leave the world at
 * a consistent point.
 */
export const SPACE_ENEMY_OFFSCREEN_MARGIN = 64;

// ── Per-tick kind selection ────────────────────────────────────────

/**
 * The spawner does ONE Poisson roll per tick (probability
 * `dt / SPACE_ENEMY_SPAWN_INTERVAL_S`). On a successful roll it makes a
 * second RNG call and walks a cumulative weight ladder to pick the kind:
 *   r < SPACE_ENEMY_FIGHTER_WEIGHT                                       → fighter
 *   r < SPACE_ENEMY_FIGHTER_WEIGHT + SPACE_ENEMY_SATELLITE_WEIGHT        → satellite
 *   else                                                                  → bomber
 *
 * At the current 0.7 / 0.1 split that's ~70% fighters, ~10% satellites,
 * ~20% bombers — fighters dominate, bombers punctuate, satellites are
 * the rare "event" enemy. DECISION: The bomber slice is the high end of
 * the range so the original BomberDrone tests that pin the kind roll to
 * 0.999 still resolve to a bomber. DECISION: The fighter slice is still
 * the [0, weight) range so any test pinning the RNG to `() => 0`
 * continues to produce a fighter — preserves every assertion pinned in
 * the FighterDrone slice.
 */
export const SPACE_ENEMY_FIGHTER_WEIGHT = 0.7;

/**
 * Satellite slice of the per-tick kind picker — see
 * {@link SPACE_ENEMY_FIGHTER_WEIGHT} for the ladder rules. Cumulative
 * upper bound for satellite is `SPACE_ENEMY_FIGHTER_WEIGHT +
 * SPACE_ENEMY_SATELLITE_WEIGHT`; everything above that lands on bomber.
 */
export const SPACE_ENEMY_SATELLITE_WEIGHT = 0.1;

// ── HomingMine ──────────────────────────────────────────────────────────────
//
// Phase 4 — Level 2-3 nebula gauntlet hazard. Drifts left with the
// starfield in IDLE; when the ship enters {@link HOMING_MINE_TRIGGER_RADIUS_PX},
// the mine transitions to ACTIVE and homes in at a constant speed.
// See ROADMAP.md Phase 4 "Level 2-3: nebula gauntlet — limited
// visibility, homing mines". Spawner + rendering integration land in
// follow-up slices.

/** Hitbox width of a homing mine in logical px. Small/compact silhouette
 *  so the player has to read fog cues to spot it before the trigger radius. */
export const HOMING_MINE_WIDTH = 14;

/** Hitbox height of a homing mine in logical px — square hitbox. */
export const HOMING_MINE_HEIGHT = 14;

/** One ship-projectile hit kills a mine. The hazard is the proximity
 *  trigger, not the durability. */
export const HOMING_MINE_HP = 1;

/** Leftward drift in IDLE relative to world space (px / s). Matches the
 *  apparent starfield drift so a stationary-looking mine reads as part
 *  of the field until it activates. */
export const HOMING_MINE_IDLE_DRIFT_PX_S = 30;

/** Linear speed in ACTIVE (px / s). Slower than the boss missile
 *  ({@link VOID_MOTHERSHIP_MISSILE_SPEED}) — the mine is a positional
 *  threat, not a fast strike. */
export const HOMING_MINE_ACTIVE_SPEED_PX_S = 110;

/** Maximum heading turn per second in ACTIVE (radians / s). Low enough
 *  that the ship can out-flank a triggered mine with timely repositioning. */
export const HOMING_MINE_TURN_RATE_RAD_S = 1.6;

/** Centre-to-centre distance (px) at which an IDLE mine flips to ACTIVE
 *  and starts homing. Smaller than the viewport width so the player
 *  isn't pre-triggered before reaching the hazard. */
export const HOMING_MINE_TRIGGER_RADIUS_PX = 90;

/** Seconds the mine lives after going ACTIVE before self-destructing.
 *  Mirrors the boss-missile TTL guard so a mine that can't catch the
 *  ship can't orbit forever and pin the pool. */
export const HOMING_MINE_ACTIVE_TTL_S = 6;

/** Offscreen-LEFT cull margin (px). Mines clear the viewport's left
 *  edge by this much before pool release. */
export const HOMING_MINE_OFFSCREEN_MARGIN_PX = 32;

/** Concurrent active-mine cap. Also the pool size — the spawner drops
 *  rolls when the active list is at this many. Kept small so the player
 *  isn't swarmed; the hazard is positional, not numerical. */
export const HOMING_MINE_MAX_ACTIVE = 4;

/** Mean seconds between Poisson spawn rolls. Per-tick spawn probability
 *  is `dt / HOMING_MINE_SPAWN_INTERVAL_S`. ~2.5 s average means a
 *  steady but sparse cadence over the Nebula Gauntlet's 80 s run. */
export const HOMING_MINE_SPAWN_INTERVAL_S = 2.5;

/** Distance (px) past the camera's right edge at which fresh mines
 *  spawn. Same role as `ASTEROID_SPAWN_X_OFFSET` — the mine clears its
 *  own width plus a small buffer before becoming visible. */
export const HOMING_MINE_SPAWN_X_OFFSET = 16;

/** Minimum spawn Y (px from top of viewport). Matches
 *  `ASTEROID_SPAWN_Y_MIN` so mines and asteroids occupy the same
 *  vertical band. */
export const HOMING_MINE_SPAWN_Y_MIN = 24;

/** Vertical buffer (px) above the bottom edge for mine spawns. The
 *  spawner uses `VIEWPORT_HEIGHT - HOMING_MINE_HEIGHT -
 *  HOMING_MINE_SPAWN_Y_MAX_OFFSET` as the inclusive upper bound. */
export const HOMING_MINE_SPAWN_Y_MAX_OFFSET = 24;
